import React, { ReactNode, useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, TableContainer, Box, Center, Spinner, Flex, Text, Input, InputGroup, InputLeftElement, IconButton, Checkbox, Menu, MenuButton, MenuList, MenuItem, Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow, Select, HStack, VStack, Tooltip, Badge, Portal, useOutsideClick, Skeleton, } from "@chakra-ui/react";
import { SearchIcon, SettingsIcon, LockIcon, UnlockIcon, ChevronRightIcon, TriangleUpIcon, TriangleDownIcon, } from "@chakra-ui/icons";
import MyDiv from "./CustomTables.style";
import CustomPagination from "../CustomPagination";


type FilterType = "text" | "number" | "date" | "boolean" | "select";
type TextOp = "contains" | "notContains" | "startsWith" | "endsWith" | "equals" | "notEquals" | "empty" | "notEmpty";
type NumOp = "equals" | "notEquals" | "gt" | "lt" | "gte" | "lte" | "between";
type DateOp = "equals" | "before" | "after" | "between" | "today" | "yesterday" | "thisWeek" | "thisMonth" | "lastMonth" | "thisYear";

interface FilterState {
    op?: string;
    value?: any;
    value2?: any; // for "between"
    values?: (string | number)[]; // for select
}

interface ColumnProps<T> {
    field?: keyof T | string;
    header?: ReactNode;
    body?: (rowData: T, index: number) => ReactNode;
    width?: number;         // px
    minWidth?: number;
    sortable?: boolean;
    filterable?: boolean;
    filterType?: FilterType;
    frozen?: "left" | "right" | boolean; // true = left
    hidden?: boolean;       // default hidden (still togglable from column chooser)
    align?: "left" | "center" | "right";
}

export function Column<T>(_props: ColumnProps<T>) {
    return null;
}

interface SortRule {
    field: string;
    dir: "asc" | "desc";
}

export interface ILazyFilter {
    op?: string;
    value?: any;
    value2?: any;
    values?: (string | number)[];
}

export interface ILazyParams {
    first: number;              // offset, e.g. (page-1)*rows
    rows: number;                // page size
    sortField?: string;          // primary sort field (back-compat, single-sort backends)
    sortOrder?: 1 | -1;          // 1 = asc, -1 = desc
    multiSortMeta?: SortRule[];  // full multi-sort list, in priority order
    filters?: Record<string, ILazyFilter>;
    globalFilter?: string;
}

interface CustomTableProps<T> {
    value: T[];
    children: React.ReactNode;
    headerColor?: string;
    borderColor?: string;
    loading?: boolean;
    emptyMessage?: string;
    /** Number of shimmering skeleton rows to show while loading with no data yet. Default 6. */
    loadingSkeletonRows?: number;

    /** Unique key for this table used to persist column layout (order/hidden/frozen/widths) to localStorage. Omit to disable persistence. */
    preferenceKey?: string;

    /** Enable the toolbar (global search + column chooser). Default true if any advanced feature is used. */
    showToolbar?: boolean;
    showGlobalSearch?: boolean;
    globalSearchPlaceholder?: string;

    /**
     * LAZY / SERVER-SIDE MODE.
     * When true, `value` is assumed to already be the current page of filtered+sorted data
     * from your API. The table will NOT filter/sort/search on the client. Instead every time
     * sort, filter, or global search changes, `onLazyParamsChange` fires with the full
     * ILazyParams payload so you can refetch (same shape as your existing ILazyParams usage).
     */
    lazy?: boolean;
    lazyParams?: ILazyParams;
    onLazyParamsChange?: (params: ILazyParams) => void;
    /** Debounce (ms) before firing onLazyParamsChange for text filters / global search. Default 400. */
    lazyDebounceMs?: number;
    totalRecords?: number; // used only to label "X results" when lazy

    /** Row selection */
    selectable?: "single" | "multiple";
    selectedRows?: T[];
    onSelectionChange?: (rows: T[]) => void;
    rowKey?: (row: T, index: number) => string | number;

    /** Group rows by a field (client-side; only sensible for the current page in lazy mode) */
    groupByField?: keyof T | string;

    /** Row expansion */
    expandable?: boolean;
    rowExpansionTemplate?: (row: T, index: number) => ReactNode;

    /**
     * Show the CustomPagination footer. Works in both modes:
     * - lazy: page/rowsPerPage changes flow through onLazyParamsChange (server does the slicing).
     * - non-lazy: page/rowsPerPage changes slice `processed` locally, no extra plumbing needed.
     * Default: true if `totalRecords` is passed (lazy) — otherwise off, since most existing
     * tables in the app render everything unpaginated today.
     */
    showPagination?: boolean;
    rowsPerPageOptions?: number[];
    defaultRowsPerPage?: number;

    /** Max height of the scrollable table body. Pass null/undefined to let it grow to fit all rows (no inner scroll). Default "600px". */
    maxTableHeight?: string | null;
}


/* ----------------------------- Helpers ----------------------------- */

function getVal<T>(row: T, field?: string) {
    if (!field) return undefined;
    return field.split(".").reduce((acc: any, k) => (acc == null ? acc : acc[k]), row as any);
}

function textMatch(v: any, f: FilterState) {
    const s = (v ?? "").toString().toLowerCase();
    const q = (f.value ?? "").toString().toLowerCase();
    switch (f.op as TextOp) {
        case "contains": return s.includes(q);
        case "notContains": return !s.includes(q);
        case "startsWith": return s.startsWith(q);
        case "endsWith": return s.endsWith(q);
        case "equals": return s === q;
        case "notEquals": return s !== q;
        case "empty": return s.length === 0;
        case "notEmpty": return s.length > 0;
        default: return true;
    }
}

function numMatch(v: any, f: FilterState) {
    const n = Number(v);
    const target = Number(f.value);
    const target2 = Number(f.value2);
    if (Number.isNaN(n)) return false;
    switch (f.op as NumOp) {
        case "equals": return n === target;
        case "notEquals": return n !== target;
        case "gt": return n > target;
        case "lt": return n < target;
        case "gte": return n >= target;
        case "lte": return n <= target;
        case "between": return n >= target && n <= target2;
        default: return true;
    }
}

function startOfDay(d: Date) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }

function dateMatch(v: any, f: FilterState) {
    if (!v) return false;
    const d = startOfDay(new Date(v));
    const today = startOfDay(new Date());
    const val = f.value ? startOfDay(new Date(f.value)) : null;
    const val2 = f.value2 ? startOfDay(new Date(f.value2)) : null;
    switch (f.op as DateOp) {
        case "equals": return val ? d.getTime() === val.getTime() : true;
        case "before": return val ? d.getTime() < val.getTime() : true;
        case "after": return val ? d.getTime() > val.getTime() : true;
        case "between": return val && val2 ? d.getTime() >= val.getTime() && d.getTime() <= val2.getTime() : true;
        case "today": return d.getTime() === today.getTime();
        case "yesterday": {
            const y = new Date(today); y.setDate(y.getDate() - 1);
            return d.getTime() === y.getTime();
        }
        case "thisWeek": {
            const start = new Date(today); start.setDate(start.getDate() - start.getDay());
            const end = new Date(start); end.setDate(end.getDate() + 6);
            return d.getTime() >= start.getTime() && d.getTime() <= end.getTime();
        }
        case "thisMonth":
            return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
        case "lastMonth": {
            const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            return d.getMonth() === lm.getMonth() && d.getFullYear() === lm.getFullYear();
        }
        case "thisYear": return d.getFullYear() === today.getFullYear();
        default: return true;
    }
}

function boolMatch(v: any, f: FilterState) {
    if (f.value === undefined || f.value === null || f.value === "") return true;
    return Boolean(v) === (f.value === "true" || f.value === true);
}

function selectMatch(v: any, f: FilterState) {
    if (!f.values || f.values.length === 0) return true;
    return f.values.includes(v);
}

function applyFilter(v: any, type: FilterType, f: FilterState) {
    if (!f || (f.op === undefined && f.values === undefined)) return true;
    switch (type) {
        case "number": return numMatch(v, f);
        case "date": return dateMatch(v, f);
        case "boolean": return boolMatch(v, f);
        case "select": return selectMatch(v, f);
        default: return textMatch(v, f);
    }
}

/* ----------------------------- Filter popover UI ----------------------------- */

const TEXT_OPS: { value: TextOp; label: string }[] = [
    { value: "contains", label: "Contains" },
    { value: "notContains", label: "Does Not Contain" },
    { value: "startsWith", label: "Starts With" },
    { value: "endsWith", label: "Ends With" },
    { value: "equals", label: "Equals" },
    { value: "notEquals", label: "Not Equals" },
    { value: "empty", label: "Empty" },
    { value: "notEmpty", label: "Not Empty" },
];
const NUM_OPS: { value: NumOp; label: string }[] = [
    { value: "equals", label: "Equals" },
    { value: "notEquals", label: "Not Equals" },
    { value: "gt", label: "Greater Than" },
    { value: "lt", label: "Less Than" },
    { value: "gte", label: "Greater Than or Equal" },
    { value: "lte", label: "Less Than or Equal" },
    { value: "between", label: "Between" },
];
const DATE_OPS: { value: DateOp; label: string }[] = [
    { value: "equals", label: "Equals" },
    { value: "before", label: "Before" },
    { value: "after", label: "After" },
    { value: "between", label: "Between" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "thisWeek", label: "This Week" },
    { value: "thisMonth", label: "This Month" },
    { value: "lastMonth", label: "Last Month" },
    { value: "thisYear", label: "This Year" },
];

function FilterPopover({ type, filter, onChange, selectOptions, }: { type: FilterType; filter: FilterState; onChange: (f: FilterState) => void; selectOptions?: (string | number)[]; }) {
    const [local, setLocal] = useState<FilterState>(filter || {});

    useEffect(() => {
        setLocal(filter || {});
    }, [filter]);

    const commit = (next: FilterState) => {
        setLocal(next);
        onChange(next);
    };

    // Select Filter
    if (type === "select") {
        return (
            <VStack align="stretch" spacing={1} maxH="200px" overflowY="auto">
                {(selectOptions || []).map((opt) => (
                    <Checkbox key={opt} size="sm" isChecked={(local.values || []).includes(opt)}
                        onChange={(e) => {
                            const cur = new Set(local.values || []);
                            if (e.target.checked) cur.add(opt);
                            else cur.delete(opt);
                            commit({ ...local, values: Array.from(cur), });
                        }}
                    >
                        {String(opt)}
                    </Checkbox>
                ))}
            </VStack>
        );
    }

    // Boolean Filter
    if (type === "boolean") {
        return (
            <Select size="sm" placeholder="Any" value={local.value ?? ""} onChange={(e) => commit({ ...local, value: e.target.value, })}>
                <option value="true">Yes</option>
                <option value="false">No</option>
            </Select>
        );
    }
    const ops = type === "number" ? NUM_OPS : type === "date" ? DATE_OPS : TEXT_OPS;

    const noValueOps = ["empty", "notEmpty", "today", "yesterday", "thisWeek", "thisMonth", "lastMonth", "thisYear",];
    const needsValue = !noValueOps.includes(local.op || "");
    const needsValue2 = local.op === "between";

    return (
        <VStack align="stretch" spacing={2}>
            <Select size="sm" placeholder="Select condition" value={local.op || ""}
                onChange={(e) => {
                    const next = { ...local, op: e.target.value, value: "", value2: "" };
                    setLocal(next);
                    if (noValueOps.includes(next.op!)) {
                        onChange(next);
                    }
                }}
            >
                {ops.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </Select>

            {!local.op && (<Text fontSize="xs" color="gray.500">Select a condition first</Text>)}

            {needsValue && (
                <>
                    <Input size="sm" type={type === "number" ? "number" : type === "date" ? "date" : "text"} value={local.value ?? ""} placeholder="Value" disabled={!local.op} onChange={(e) => { const next = { ...local, value: e.target.value }; commit(next); }} />
                </>
            )}

            {needsValue2 && (
                <Input size="sm" type={type === "number" ? "number" : "date"} value={local.value2 ?? ""} placeholder="To" onChange={(e) => { const next = { ...local, value2: e.target.value }; commit(next); }} />
            )}
        </VStack>
    );
}

/* ----------------------------- Main component ----------------------------- */

export function CustomTables<T extends Record<string, any>>({
    value,
    children,
    headerColor = "Black",
    loading,
    emptyMessage,
    loadingSkeletonRows = 20,
    preferenceKey,
    showToolbar = true,
      showGlobalSearch = true,
    globalSearchPlaceholder = "Search...",
    lazy = false,
    lazyParams,
    onLazyParamsChange,
    lazyDebounceMs = 1000,
    totalRecords,
    selectable,
    selectedRows,
    onSelectionChange,
    rowKey,
    groupByField,
    expandable,
    rowExpansionTemplate,
    showPagination,
    rowsPerPageOptions,
    defaultRowsPerPage = 20,
    maxTableHeight = "600px",
}: CustomTableProps<T>) {
    const rawColumns = React.useMemo(
        () =>
            React.Children.toArray(children)
                .filter((child): child is React.ReactElement<ColumnProps<T>> => React.isValidElement(child) && child.type === Column)
                .map((col, i) => ({ id: (col.props.field as string) || `col_${i}`, ...col.props })),
        [children]
    );

    const storageKey = preferenceKey ? `customTable:${preferenceKey}` : null;

    const loadPrefs = () => {
        if (!storageKey) return null;
        try {
            const raw = localStorage.getItem(storageKey);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    };
    const prefs = useRef(loadPrefs());

    /* ---------- state ---------- */
    const [order, setOrder] = useState<string[]>(prefs.current?.order || rawColumns.map((c) => c.id));
    const [hidden, setHidden] = useState<Record<string, boolean>>(prefs.current?.hidden || Object.fromEntries(rawColumns.filter((c) => c.hidden).map((c) => [c.id, true])));
    const [frozen, setFrozen] = useState<Record<string, "left" | "right" | boolean | undefined>>(
        prefs.current?.frozen ||
        Object.fromEntries(rawColumns.filter((c) => c.frozen).map((c) => [c.id, c.frozen === "right" ? "right" : "left"]))
    );
    const [widths, setWidths] = useState<Record<string, number>>(prefs.current?.widths || Object.fromEntries(rawColumns.filter((c) => c.width).map((c) => [c.id, c.width!])));
    const [sorts, setSorts] = useState<SortRule[]>(lazy ? lazyParams?.multiSortMeta || [] : prefs.current?.sorts || []);
    const [filters, setFilters] = useState<Record<string, FilterState>>(lazy ? (lazyParams?.filters as Record<string, FilterState>) || {} : prefs.current?.filters || {});
    const [globalSearch, setGlobalSearch] = useState(lazy ? lazyParams?.globalFilter || "" : "");

    /* ---------- lazy: emit params to parent whenever sort/filter/search change ---------- */
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const emitLazy = useCallback(
        (overrides: Partial<ILazyParams> = {}, debounce = false) => {
            if (!lazy || !onLazyParamsChange) return;
            const fire = () => {
                onLazyParamsChange({ first: lazyParams?.first ?? 0, rows: lazyParams?.rows ?? 20, sortField: sorts[0]?.field, sortOrder: sorts[0] ? (sorts[0].dir === "asc" ? 1 : -1) : undefined, multiSortMeta: sorts, filters, globalFilter: globalSearch, ...overrides, });
            };
            if (debounce) {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(fire, lazyDebounceMs);
            } else {
                fire();
            }
        },
        [lazy, onLazyParamsChange, sorts, filters, globalSearch, lazyParams?.first, lazyParams?.rows, lazyDebounceMs]
    );
    const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(new Set());
    const [expandedKeys, setExpandedKeys] = useState<Set<string | number>>(new Set());
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; colId: string } | null>(null);
    const resizingRef = useRef<{ id: string; startX: number; startW: number } | null>(null);
    const dragColRef = useRef<string | null>(null);

    useEffect(() => {
        if (!storageKey) return;
        const toSave = { order, hidden, frozen, widths, sorts, filters };
        try { localStorage.setItem(storageKey, JSON.stringify(toSave)); } catch { /* ignore */ }
    }, [storageKey, order, hidden, frozen, widths, sorts, filters]);

    // keep order in sync if columns change
    useEffect(() => {
        setOrder((prev) => {
            const known = rawColumns.map((c) => c.id);
            const kept = prev.filter((id) => known.includes(id));
            const added = known.filter((id) => !kept.includes(id));
            return [...kept, ...added];
        });
    }, [rawColumns.map((c) => c.id).join(",")]);

    const columnsById = useMemo(() => Object.fromEntries(rawColumns.map((c) => [c.id, c])), [rawColumns]);

    const visibleColumns = useMemo(
        () => order.filter((id) => !hidden[id] && columnsById[id]).map((id) => columnsById[id]),
        [order, hidden, columnsById]
    );

    const leftFrozen = visibleColumns.filter((c) => frozen[c.id] === "left" || frozen[c.id] === true);
    const rightFrozen = visibleColumns.filter((c) => frozen[c.id] === "right");
    const normalCols = visibleColumns.filter((c) => !frozen[c.id]);
    const orderedForRender = [...leftFrozen, ...normalCols, ...rightFrozen];

    const colWidth = (id: string) => widths[id] || columnsById[id]?.width || columnsById[id]?.minWidth || 160;
    const leftOffset = (id: string) => {
        let off = expandable ? 40 : 0;
        if (selectable) off += 40;
        for (const c of leftFrozen) {
            if (c.id === id) return off;
            off += colWidth(c.id);
        }
        return off;
    };
    const rightOffset = (id: string) => {
        let off = 0;
        for (let i = rightFrozen.length - 1; i >= 0; i--) {
            if (rightFrozen[i].id === id) return off;
            off += colWidth(rightFrozen[i].id);
        }
        return off;
    };

    const selectOptionsByCol = useMemo(() => {
        const map: Record<string, (string | number)[]> = {};
        rawColumns.forEach((c) => {
            if (c.filterType === "select" && c.field) {
                const set = new Set<string | number>();
                value.forEach((row) => { const v = getVal(row, c.field as string); if (v !== undefined && v !== null) set.add(v); });
                map[c.id] = Array.from(set);
            }
        });
        return map;
    }, [rawColumns, value]);

    const processed = useMemo(() => {
        if (lazy) return value;

        let rows = value;

        rows = rows.filter((row) =>
            rawColumns.every((c) => {
                const f = filters[c.id];
                if (!c.filterable || !f) return true;
                return applyFilter(getVal(row, c.field as string), c.filterType || "text", f);
            })
        );

        if (globalSearch.trim()) {
            const q = globalSearch.toLowerCase();
            rows = rows.filter((row) =>
                visibleColumns.some((c) => {
                    const v = getVal(row, c.field as string);
                    return v !== undefined && v !== null && v.toString().toLowerCase().includes(q);
                })
            );
        }

        // multi-sort
        if (sorts.length) {
            rows = [...rows].sort((a, b) => {
                for (const s of sorts) {
                    const av = getVal(a, s.field);
                    const bv = getVal(b, s.field);
                    let cmp = 0;
                    if (av == null && bv == null) cmp = 0;
                    else if (av == null) cmp = -1;
                    else if (bv == null) cmp = 1;
                    else if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
                    else cmp = av.toString().localeCompare(bv.toString(), undefined, { numeric: true });
                    if (cmp !== 0) return s.dir === "asc" ? cmp : -cmp;
                }
                return 0;
            });
        }
        return rows;
    }, [lazy, value, rawColumns, filters, globalSearch, visibleColumns, sorts]);

    /* ---------- pagination (works for both lazy and client-side modes) ---------- */
    const paginationEnabled = showPagination ?? lazy; // default on for lazy tables, off otherwise
    const [localPage, setLocalPage] = useState(0); // 0-indexed, only used when !lazy
    const [localRows, setLocalRows] = useState(defaultRowsPerPage);

    const currentPage = lazy ? Math.floor((lazyParams?.first ?? 0) / (lazyParams?.rows || defaultRowsPerPage)) : localPage;
    const currentRowsPerPage = lazy ? (lazyParams?.rows || defaultRowsPerPage) : localRows;
    const effectiveTotal = lazy ? totalRecords ?? 0 : processed.length;
    const pageCount = paginationEnabled ? Math.max(1, Math.ceil(effectiveTotal / currentRowsPerPage)) : 1;

    const handlePageClick = ({ selected }: { selected: number }) => {
        if (lazy) {
            emitLazy({ first: selected * currentRowsPerPage });
        } else {
            setLocalPage(selected);
        }
    };

    const handleRowsPerPageChange = (nextRows: number) => {
        if (lazy) {
            emitLazy({ rows: nextRows, first: 0 });
        } else {
            setLocalRows(nextRows);
            setLocalPage(0);
        }
    };

    // Rows actually rendered in the body: `value` as-is in lazy mode (server already sliced it),
    // or a local slice of `processed` in client-side mode.
    const displayRows = !paginationEnabled ? processed : lazy ? processed : processed.slice(localPage * localRows, (localPage + 1) * localRows);

    const grouped = useMemo(() => {
        if (!groupByField) return null;
        const map = new Map<string, T[]>();
        displayRows.forEach((row) => {
            const key = String(getVal(row, groupByField as string) ?? "—");
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(row);
        });
        return map;
    }, [displayRows, groupByField]);

    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

    /* ---------- sorting handlers ---------- */
    const toggleSort = (field: string, additive: boolean) => {
        setSorts((prev) => {
            const existing = prev.find((s) => s.field === field);
            let next: SortRule[];
            if (!additive) {
                if (!existing) next = [{ field, dir: "asc" }];
                else if (existing.dir === "asc") next = [{ field, dir: "desc" }];
                else next = [];
            } else if (!existing) {
                next = [...prev, { field, dir: "asc" }];
            } else if (existing.dir === "asc") {
                next = prev.map((s) => (s.field === field ? { ...s, dir: "desc" } : s));
            } else {
                next = prev.filter((s) => s.field !== field);
            }
            if (lazy) emitLazy({ multiSortMeta: next, sortField: next[0]?.field, sortOrder: next[0] ? (next[0].dir === "asc" ? 1 : -1) : undefined, first: 0 });
            return next;
        });
    };

    /* ---------- resize handlers ---------- */
    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (!resizingRef.current) return;
            const { id, startX, startW } = resizingRef.current;
            const next = Math.max(60, startW + (e.clientX - startX));
            setWidths((w) => ({ ...w, [id]: next }));
        };
        const onUp = () => { resizingRef.current = null; };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, []);

    const autoFit = (id: string) => {
        setWidths((w) => { const c = { ...w }; delete c[id]; return c; });
    };

    /* ---------- drag reorder ---------- */
    const onDragStart = (id: string) => { dragColRef.current = id; };
    const onDrop = (targetId: string) => {
        const src = dragColRef.current;
        dragColRef.current = null;
        if (!src || src === targetId) return;
        setOrder((prev) => {
            const next = prev.filter((id) => id !== src);
            const idx = next.indexOf(targetId);
            next.splice(idx, 0, src);
            return next;
        });
    };

    /* ---------- selection ---------- */
    const getKey = useCallback((row: T, idx: number) => (rowKey ? rowKey(row, idx) : idx), [rowKey]);

    useEffect(() => {
        if (selectedRows && rowKey) {
            setSelectedKeys(new Set(selectedRows.map((r, i) => rowKey(r, i))));
        }
    }, [selectedRows, rowKey]);

    const emitSelection = (keys: Set<string | number>) => {
        setSelectedKeys(keys);
        if (onSelectionChange) {
            const rows = processed.filter((r, i) => keys.has(getKey(r, i)));
            onSelectionChange(rows);
        }
    };

    const toggleRow = (row: T, idx: number) => {
        const key = getKey(row, idx);
        if (selectable === "single") {
            emitSelection(new Set([key]));
            return;
        }
        const next = new Set(selectedKeys);
        if (next.has(key)) next.delete(key); else next.add(key);
        emitSelection(next);
    };

    const allSelected = processed.length > 0 && processed.every((r, i) => selectedKeys.has(getKey(r, i)));
    const toggleAll = () => {
        if (allSelected) emitSelection(new Set());
        else emitSelection(new Set(processed.map((r, i) => getKey(r, i))));
    };

    /* ---------- context menu ---------- */
    const ctxRef = useRef<HTMLDivElement>(null);
    useOutsideClick({ ref: ctxRef, handler: () => setContextMenu(null) });

    const activeFilterCount = Object.keys(filters).filter((k) => {
        const f = filters[k];
        return f && (f.op || (f.values && f.values.length));
    }).length;

    /* ---------- render helpers ---------- */

    // Deterministic, opaque row background — replaces "inherit" for sticky cells so
    // scrolled-under content never bleeds through frozen columns. Mirrors Chakra's
    // variant="striped" coloring (odd rows shaded) plus the selected-row green.
    const rowBg = (rowIndex: number, isSelected: boolean) => {
        if (isSelected) return "#F0FFF4"; // chakra green.50
        return rowIndex % 2 === 1 ? "#F7FAFC" : "#FFFFFF"; // chakra gray.50 / white
    };

    const renderHeaderCell = (col: any) => {
        const sortIdx = sorts.findIndex((s) => s.field === col.id);
        const sortRule = sorts[sortIdx];
        const isFrozenCol = !!frozen[col.id];
        const isLastLeftFrozen = leftFrozen.length > 0 && leftFrozen[leftFrozen.length - 1]?.id === col.id;
        const isFirstRightFrozen = rightFrozen.length > 0 && rightFrozen[0]?.id === col.id;
        const style: React.CSSProperties = {
            width: colWidth(col.id),
            minWidth: colWidth(col.id),
            position: isFrozenCol ? "sticky" : undefined,
            left: frozen[col.id] === "left" || frozen[col.id] === true ? leftOffset(col.id) : undefined,
            right: frozen[col.id] === "right" ? rightOffset(col.id) : undefined,
            zIndex: isFrozenCol ? 3 : 2,
            background: "#F7FAFC", // gray.50 — hardcoded, no parent bg to meaningfully "inherit" in the header row
            boxShadow: isLastLeftFrozen
                ? "2px 0 4px -2px rgba(0,0,0,0.15)"
                : isFirstRightFrozen
                    ? "-2px 0 4px -2px rgba(0,0,0,0.15)"
                    : undefined, // visible edge marking where frozen columns end
        };
        return (
            <Th
                key={col.id}
                color={headerColor}
                fontWeight="semibold"
                style={style}
                position="sticky"
                top={0}
                bg="gray.50"
                draggable
                role="group"
                onDragStart={() => onDragStart(col.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(col.id)}
                onContextMenu={(e) => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, colId: col.id }); }}
                userSelect="none"
                whiteSpace="nowrap"
            >
                <Flex align="center" gap={1}>
                    <Box flex="1" cursor={col.sortable ? "pointer" : "default"} onClick={(e) => col.sortable && toggleSort(col.id, e.shiftKey)} >
                        {col.header}
                    </Box>
                    {col.sortable && (
                        sortRule ? (
                            <Badge fontSize="0.6rem" colorScheme="green">
                                {sortRule.dir === "asc" ? <TriangleUpIcon boxSize={2} /> : <TriangleDownIcon boxSize={2} />}
                                {sorts.length > 1 ? sortIdx + 1 : ""}
                            </Badge>
                        ) : (
                            <Tooltip label="Click to sort, Shift+Click to add to multi-sort" placement="top" hasArrow openDelay={400}>
                                <Box color="gray.300" _groupHover={{ color: "gray.500" }} display="flex" flexDir="column" lineHeight="0.6">
                                    <TriangleUpIcon boxSize={2} />
                                    <TriangleDownIcon boxSize={2} />
                                </Box>
                            </Tooltip>
                        )
                    )}
                    {frozen[col.id] ? <LockIcon boxSize={2.5} color="green.500" /> : null}
                    {col.filterable && (
                        <Popover placement="bottom-start" isLazy>
                            <PopoverTrigger>
                                <IconButton aria-label="Filter" icon={<SearchIcon />} size="xs" variant="ghost" color={filters[col.id]?.op || filters[col.id]?.values?.length ? "green.500" : "gray.400"} />
                            </PopoverTrigger>
                            <Portal>
                                <PopoverContent w="230px" p={2}>
                                    <PopoverArrow />
                                    <PopoverBody>
                                        <FilterPopover type={col.filterType || "text"} filter={filters[col.id] || {}} selectOptions={selectOptionsByCol[col.id]}
                                            onChange={(f) => {
                                                setFilters((prev) => {
                                                    const next = { ...prev, [col.id]: f };
                                                    if (lazy) emitLazy({ filters: next, first: 0 }, true);
                                                    return next;
                                                });
                                            }}
                                        />
                                    </PopoverBody>
                                </PopoverContent>
                            </Portal>
                        </Popover>
                    )}
                </Flex>
                <Box position="absolute" right={0} top={0} bottom={0} width="4px" cursor="col-resize" onMouseDown={(e) => { resizingRef.current = { id: col.id, startX: e.clientX, startW: colWidth(col.id) }; }} onDoubleClick={() => autoFit(col.id)} />
            </Th>
        );
    };

    const renderBodyCell = (col: any, row: T, rowIndex: number, bg: string) => {
        const isFrozenCol = !!frozen[col.id];
        const isLastLeftFrozen = leftFrozen.length > 0 && leftFrozen[leftFrozen.length - 1]?.id === col.id;
        const isFirstRightFrozen = rightFrozen.length > 0 && rightFrozen[0]?.id === col.id;
        const style: React.CSSProperties = {
            width: colWidth(col.id),
            minWidth: colWidth(col.id),
            position: isFrozenCol ? "sticky" : undefined,
            left: frozen[col.id] === "left" || frozen[col.id] === true ? leftOffset(col.id) : undefined,
            right: frozen[col.id] === "right" ? rightOffset(col.id) : undefined,
            zIndex: isFrozenCol ? 1 : undefined,
            background: isFrozenCol ? bg : "inherit",
            boxShadow: isLastLeftFrozen ? "2px 0 4px -2px rgba(0,0,0,0.15)" : isFirstRightFrozen ? "-2px 0 4px -2px rgba(0,0,0,0.15)" : undefined,
        };
        const content = col.body ? col.body(row, rowIndex) : (row as any)[col.field as string];
        return (
            <Td key={col.id} className="font-poppins text_regular text_sm" style={style} textAlign={col.align || "left"} isTruncated maxW={colWidth(col.id)}>
                <Tooltip label={typeof content === "string" || typeof content === "number" ? content : undefined} openDelay={500} hasArrow>
                    <Box isTruncated>{content}</Box>
                </Tooltip>
            </Td>
        );
    };

    const renderRows = (rows: T[], startIndex: number) =>
        rows.map((row, i) => {
            const rowIndex = startIndex + i;
            const key = getKey(row, rowIndex);
            const isSelected = selectedKeys.has(key);
            const isExpanded = expandedKeys.has(key);
            const bg = rowBg(rowIndex, isSelected);

            return (
                <React.Fragment key={key}>
                    <Tr bg={isSelected ? "green.50" : undefined} _hover={{ bg: "gray.50" }}>
                        {expandable && (
                            <Td width="40px" position="sticky" left={0} bg={bg} zIndex={1}>
                                <IconButton aria-label="Expand" size="xs" variant="ghost" icon={<ChevronRightIcon transform={isExpanded ? "rotate(90deg)" : undefined} />} onClick={() => { const next = new Set(expandedKeys); if (next.has(key)) next.delete(key); else next.add(key); setExpandedKeys(next); }} />
                            </Td>
                        )}
                        {selectable && (
                            <Td width="40px" position="sticky" left={expandable ? 40 : 0} bg={bg} zIndex={1}>
                                <Checkbox isChecked={isSelected} onChange={() => toggleRow(row, rowIndex)} />
                            </Td>
                        )}
                        {orderedForRender.map((col) => renderBodyCell(col, row, rowIndex, bg))}
                    </Tr>
                    {expandable && isExpanded && rowExpansionTemplate && (
                        <Tr>
                            <Td colSpan={orderedForRender.length + (selectable ? 1 : 0) + 1} bg="gray.50">
                                {rowExpansionTemplate(row, rowIndex)}
                            </Td>
                        </Tr>
                    )}
                </React.Fragment>
            );
        });

    return (
        <MyDiv>
            {showToolbar && (
                <Flex justify="space-between" align="center" mb={2} gap={2} flexWrap="wrap">
                    {showGlobalSearch ? (
                        <InputGroup maxW="280px" size="sm">
                            <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
                            <Input placeholder={globalSearchPlaceholder} value={globalSearch}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setGlobalSearch(v);
                                    if (lazy) emitLazy({ globalFilter: v, first: 0 }, true);
                                }}
                                bg="white"
                            />
                        </InputGroup>
                    ) : (
                        <Box /> // keeps space-between layout balanced so column-picker/badges stay right-aligned
                    )}
                    <HStack spacing={2}>
                        {activeFilterCount > 0 && (
                            <Badge colorScheme="green" cursor="pointer" onClick={() => { setFilters({}); if (lazy) emitLazy({ filters: {}, first: 0 }); }}>
                                {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""} × clear
                            </Badge>
                        )}
                        {sorts.length > 0 && (
                            <Badge colorScheme="blue" cursor="pointer" onClick={() => { setSorts([]); if (lazy) emitLazy({ multiSortMeta: [], sortField: undefined, sortOrder: undefined, first: 0 }); }}>
                                {sorts.length} sort{sorts.length > 1 ? "s" : ""} × clear
                            </Badge>
                        )}
                        <Menu closeOnSelect={false}>
                            <MenuButton as={IconButton} icon={<SettingsIcon />} size="sm" variant="outline" aria-label="Columns" />
                            <MenuList maxH="300px" overflowY="auto" zIndex={20}>
                                {rawColumns.map((c) => (
                                    <MenuItem key={c.id} closeOnSelect={false}>
                                        <Flex align="center" justify="space-between" w="100%">
                                            <Checkbox isChecked={!hidden[c.id]} onChange={(e) => setHidden((h) => ({ ...h, [c.id]: !e.target.checked }))}>{typeof c.header === "string" ? c.header : c.id}</Checkbox>
                                            <IconButton aria-label="Freeze" size="xs" variant="ghost" color={frozen[c.id] ? "green.500" : "gray.400"} icon={frozen[c.id] ? <LockIcon /> : <UnlockIcon />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFrozen((f) => ({ ...f, [c.id]: f[c.id] ? undefined : "left" }));
                                                }}
                                            />
                                        </Flex>
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </Menu>
                    </HStack>
                </Flex>
            )}

            <Box position="relative">
                {loading && displayRows.length > 0 && (
                    <Center position="absolute" top={0} left={0} right={0} bottom={0} bg="whiteAlpha.700" zIndex={5} borderRadius="md">
                        <Spinner thickness="3px" speed="0.65s" emptyColor="gray.200" color="#006838" size="lg" />
                    </Center>
                )}
                <TableContainer mt={4} borderRadius="md" boxShadow="sm" bg="#fff" maxH={maxTableHeight || undefined} overflow={maxTableHeight ? "auto" : "visible"}>
                    <Table variant="striped" size="sm">
                        <Thead className="table_header font-poppins text_medium text_sm">
                            <Tr className="font-poppins text_regular text_md">
                                {expandable && <Th position="sticky" left={0} top={0} zIndex={4} bg="gray.50" width="40px" />}
                                {selectable && (
                                    <Th position="sticky" left={expandable ? 40 : 0} top={0} zIndex={4} bg="gray.50" width="40px">
                                        {selectable === "multiple" && <Checkbox isChecked={allSelected} onChange={toggleAll} />}
                                    </Th>
                                )}
                                {orderedForRender.map(renderHeaderCell)}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {loading && processed.length === 0 ? (
                                // skeleton rows
                                Array.from({ length: loadingSkeletonRows }).map((_, r) => (
                                    <Tr key={`skeleton-${r}`}>
                                        {expandable && <Td><Skeleton height="16px" /></Td>}
                                        {selectable && <Td><Skeleton height="16px" width="16px" /></Td>}
                                        {orderedForRender.map((col) => (
                                            <Td key={col.id}>
                                                <Skeleton height="16px" width={`${40 + ((r * 13 + col.id.length * 7) % 45)}%`} />
                                            </Td>
                                        ))}
                                    </Tr>
                                ))
                            ) : displayRows.length > 0 ? (
                                grouped
                                    ? Array.from(grouped.entries()).map(([groupKey, rows]) => {
                                        const collapsed = collapsedGroups.has(groupKey);
                                        return (
                                            <React.Fragment key={groupKey}>
                                                <Tr bg="green.50" fontWeight="semibold" data-group-row="true">
                                                    <Td colSpan={orderedForRender.length + (selectable ? 1 : 0) + (expandable ? 1 : 0)} cursor="pointer"
                                                        onClick={() => {
                                                            const next = new Set(collapsedGroups);
                                                            if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
                                                            setCollapsedGroups(next);
                                                        }}
                                                    >
                                                        <ChevronRightIcon transform={collapsed ? undefined : "rotate(90deg)"} mr={2} />
                                                        {String(groupByField)}: {groupKey} ({rows.length})
                                                    </Td>
                                                </Tr>
                                                {!collapsed && renderRows(rows, 0)}
                                            </React.Fragment>
                                        );
                                    })
                                    : renderRows(displayRows, 0)
                            ) : (
                                // NEW: empty state as a row — header/toolbar stay visible
                                <Tr>
                                    <Td
                                        colSpan={
                                            orderedForRender.length +
                                            (selectable ? 1 : 0) +
                                            (expandable ? 1 : 0)
                                        }
                                    >
                                        <Flex justify="center" align="center" height="300px" color="gray.500">
                                            <Text className="font-poppins text_regular text_md">
                                                {emptyMessage || "No records found"}
                                            </Text>
                                        </Flex>
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {paginationEnabled && (
                <CustomPagination pageCount={pageCount} handlePageClick={handlePageClick} rowsPerPage={currentRowsPerPage} setRowsPerPage={handleRowsPerPageChange} currentPage={currentPage} totalRecords={effectiveTotal} rowsPerPageOptions={rowsPerPageOptions} />
            )}

            {contextMenu && (
                <Portal>
                    <Box ref={ctxRef} position="fixed" top={contextMenu.y} left={contextMenu.x} bg="white" boxShadow="lg" borderRadius="md" border="1px solid" borderColor="gray.200" zIndex={50} py={1} minW="180px">
                        {[
                            { label: "Sort Ascending", action: () => { const s = [{ field: contextMenu.colId, dir: "asc" as const }]; setSorts(s); if (lazy) emitLazy({ multiSortMeta: s, sortField: s[0].field, sortOrder: 1, first: 0 }); } },
                            { label: "Sort Descending", action: () => { const s = [{ field: contextMenu.colId, dir: "desc" as const }]; setSorts(s); if (lazy) emitLazy({ multiSortMeta: s, sortField: s[0].field, sortOrder: -1, first: 0 }); } },
                            { label: "Freeze Left", action: () => setFrozen((f) => ({ ...f, [contextMenu.colId]: "left" })) },
                            { label: "Freeze Right", action: () => setFrozen((f) => ({ ...f, [contextMenu.colId]: "right" })) },
                            ...(frozen[contextMenu.colId] ? [{ label: "Unfreeze", action: () => setFrozen((f) => ({ ...f, [contextMenu.colId]: undefined })) }] : []),
                            { label: "Hide Column", action: () => setHidden((h) => ({ ...h, [contextMenu.colId]: true })) },
                            { label: "Auto Fit", action: () => autoFit(contextMenu.colId) },
                            {
                                label: "Reset Column",
                                action: () => {
                                    setFrozen((f) => ({ ...f, [contextMenu.colId]: undefined }));
                                    setHidden((h) => ({ ...h, [contextMenu.colId]: false }));
                                    autoFit(contextMenu.colId);
                                    setFilters((f) => { const c = { ...f }; delete c[contextMenu.colId]; return c; });
                                },
                            },
                        ].map((item) => (
                            <Box key={item.label} px={3} py={1.5} fontSize="sm" cursor="pointer" _hover={{ bg: "gray.100" }} onClick={() => { item.action(); setContextMenu(null); }}>{item.label}</Box>
                        ))}
                    </Box>
                </Portal>
            )}
        </MyDiv>
    );
}

export default CustomTables;